import cx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';

import { MEDIUM_BREAKPOINT } from '@/components/modular-grid/constants';
import Overlay from '@/components/Overlay';
import Portal from '@/components/Portal';
import { useWindowSize } from '@/hooks/useWindowSize';

import styles from './SearchModal.module.scss';

interface ModalPropTypes {
  componentVisible?: boolean;
  closeAnimationTrigger?: boolean;
  onModalOverlayClick?: () => void;
  children?: React.ReactNode;
}

const SearchModal: React.FC<ModalPropTypes> = props => {
  const {
    componentVisible = false,
    closeAnimationTrigger,
    children,
    onModalOverlayClick,
  } = props;

  const { width } = useWindowSize();

  const [closeAnimation, setCloseAnimation] = useState(false);
  const [isPortalVisible, setIsPortalVisible] = useState(componentVisible);

  const size = width <= MEDIUM_BREAKPOINT.max ? 'small' : 'wide';

  /**
   * We need to wait for the closing animation to be performed before we change the route
   */
  const closeActionInterval = 250;

  const handleCloseClick = useCallback(() => {
    setCloseAnimation(true);
    setIsPortalVisible(false);

    setTimeout(() => {
      onModalOverlayClick?.();
      setCloseAnimation(false);
    }, closeActionInterval);
  }, [onModalOverlayClick]);

  useEffect(() => {
    setIsPortalVisible(componentVisible);
    if (closeAnimationTrigger) {
      handleCloseClick();
    }
  }, [componentVisible, closeAnimationTrigger, handleCloseClick]);

  if (!children || (!componentVisible && closeAnimationTrigger !== true))
    return null;

  const SmallModal = (
    <div
      data-testid="search-modal-small"
      id="modal"
      className={cx(
        styles.modalMobile,
        !closeAnimation && styles.show,
        closeAnimation && styles.hide
      )}
    >
      <div className={styles.modalContent}>{children}</div>
    </div>
  );

  const LargeModal = (
    <>
      <div
        data-testid="search-modal-large"
        id="modal"
        className={cx(
          styles.modalDesktop,
          !closeAnimation && styles.show,
          closeAnimation && styles.hide
        )}
      >
        <div className={styles.modalContent}>{children}</div>
      </div>
      <Overlay componentVisible={isPortalVisible} onClick={handleCloseClick} />
    </>
  );

  return (
    <Portal
      id={'page-content'}
      children={size === 'small' ? SmallModal : LargeModal}
      childId="searchModal"
    />
  );
};

export default SearchModal;
